import React, {
  useCallback, useContext, useState, useEffect, useMemo
} from 'react'
import useBrand from '@engine/hooks/useBrand'
import Brands from '@brands/index'
import { CardInfo, Input, Typography } from '@telus/koodo-ui-kit'
import { PHONE_NUMBER_STATE, updatePhoneNumberState } from '@apps/redux/features/PhoneNumberSlice'
import PhoneNumberChangeHandlers, {
  PHONE_NUMBER_EVENTS,
  PHONE_NUMBER_ERROR_KEYS
} from '@apps/utils/ChangeHandlers/PhoneNumberChangeHandlers'
import { getById, getBySlug } from '@apps/utils/contentful'
import { useDispatch, useSelector } from 'react-redux'
import ToolTip from '@apps/components/ToolTip'
import { ApplicationContext, APPLICATION_STATE } from '@apps/contexts/ApplicationContext'
import Sites from '@sites/index'
import Heading4 from '../Typography/Heading4'
import { ANALYTICS_EVENT_TYPES } from '@/apps/utils/constants'
import MarkdownRenderer from '@/apps/components/MarkdownRenderer'
import DatalistV2 from '../DatalistV2'

const MAX_LENGTH = '15'
const MAX_LENGTH_ACCOUNT_NUMBER = '255'
const MAX_LENGTH_ALTERNATE_PHONE_NUMBER = '12'

const TransferMobile = ({ mobileRefs, mobileHandlers, pushClickAnalytics }) => {
  const dispatch = useDispatch()

  const { userType } = useSelector((state) => state.common)
  const { pageContent } = useSelector((state) => state.common.content)

  const phoneNumberState = useSelector((state) => state.phoneNumberPage)
  const [toggleTooltip, setToggleToolTip] = useState(false)
  const { [APPLICATION_STATE.CURRENT_LANGUAGE]: currentLanguage } = useContext(ApplicationContext)

  const [maxLengthAccountNumber, setMaxLengthAccountNumber] = useState(MAX_LENGTH_ACCOUNT_NUMBER)
  const [minLengthAccountNumber, setMinLengthAccountNumber] = useState(0)
  const [accountNumberRegex, setAccountNumberRegex] = useState('')
  const [accountNumberErrorMessage, setAccountNumberErrorMessage] = useState('')

  const {
    [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.WIRELESS.SELECTED_SPID]: selectedSpid,
    [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.WIRELESS.SELECTED_SPID_ERROR]: spidError,
    [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.WIRELESS.IMEI]: imeiNumber,
    [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.WIRELESS.IMEI_ERROR]: imeiError,
    [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.WIRELESS.PREVIOUS_PROVIDER_ACCOUNT_NUMBER]:
    prevProviderAccountNumber,
    [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.WIRELESS.PREVIOUS_PROVIDER_ACCOUNT_NUMBER_ERROR]:
    prevAccountNumberError,
    [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.ALTERNATE_CONTACT_NUMBER]: alternateContactNumber,
    [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.ALTERNATE_CONTACT_NUMBER_ERROR]: alternatePhoneError
  } = phoneNumberState

  const [spidValue, setSPIDValue] = useState(selectedSpid || '')

  const toolTipHandler = useCallback(() => {
    setToggleToolTip(!toggleTooltip)
    if (pushClickAnalytics) {
      pushClickAnalytics(imeiTooltip, ANALYTICS_EVENT_TYPES.POPUP)
    }
  }, [toggleTooltip])

  const toolTipBlurHandler = useCallback(() => {
    setToggleToolTip(false)
  }, [toggleTooltip])

  const isDealer = useMemo(() => {
    return userType === Sites.dealer
  }, [userType])

  const pagePrefix = isDealer ? 'Dealer' : 'Consumer'
  const phoneNumberPageContent = getById(pageContent, `phoneNumberPage${pagePrefix}`) || {}
  const { shortTextFields, longTextFields } = phoneNumberPageContent[currentLanguage] || {}

  const textContent = useMemo(() => {
    return {
      important: getBySlug(shortTextFields, 'important')?.value,
      previousProvider: getBySlug(shortTextFields, 'previousProvider')?.value,
      previousProviderRequired: getBySlug(shortTextFields, 'previousProviderRequired')?.value,
      imei: getBySlug(shortTextFields, 'imei')?.value,
      previousAccountNumber: getBySlug(shortTextFields, 'previousAccountNumber')?.value,
      or: getBySlug(shortTextFields, 'or')?.value,
      imeiTooltip: getBySlug(shortTextFields, 'imeiTooltip')?.value,
      alternateContactTitle: getBySlug(shortTextFields, 'alternateContactTitle')?.value,
      alternateInfoKoodo: getBySlug(shortTextFields, 'alternateInfoKoodo')?.value,
      alternateInfoPublic: getBySlug(shortTextFields, 'alternateInfoPublic')?.value,
      previousProviderInvalid: getBySlug(shortTextFields, 'previousProviderInvalid')?.value,
      additionalSecurityTitle: getBySlug(shortTextFields, 'additionalSecurityTitle')?.value,
      serviceProviderDate: getBySlug(longTextFields, 'serviceProviderDate')?.value,
      additionalSecurityContent: getBySlug(longTextFields, 'additionalSecurityContent')?.value,
      transferLandlineFootnote: getBySlug(shortTextFields, 'transferLandlineFootnote')?.value
    }
  }, [shortTextFields, longTextFields])

  const {
    important,
    previousProvider,
    previousProviderRequired,
    imei,
    previousAccountNumber,
    or,
    imeiTooltip,
    alternateContactTitle,
    alternateInfoKoodo,
    alternateInfoPublic,
    previousProviderInvalid,
    serviceProviderDate,
    transferLandlineFootnote
  } = textContent

  const serviceProviders = serviceProviderDate ? JSON.parse(serviceProviderDate)[currentLanguage].copy?.serviceProvider || [] : []

  const SPIDS = serviceProviders?.map(item => ({
    value: item?.value,
    name: item?.name
  }))

  const spidErrors = {
    [PHONE_NUMBER_ERROR_KEYS.SPID_REQUIRED]: previousProviderRequired,
    [PHONE_NUMBER_ERROR_KEYS.SPID_INVALID]: previousProviderInvalid
  }

  const brand = useBrand()

  const bothInputsEmpty = !prevProviderAccountNumber && !imeiNumber
  const requiredError = bothInputsEmpty && (prevAccountNumberError || imeiError)
  const accountFieldError = prevProviderAccountNumber && prevAccountNumberError
  const imeiFieldError = imeiNumber && imeiError
  const showAccountValidation = requiredError || accountFieldError
  const showImeiValidation = requiredError || imeiFieldError
  const errorMessage = accountFieldError || imeiFieldError || requiredError

  const {
    prevProvRef, imeiRef, accountRef, alternateRef
  } = mobileRefs

  const {
    handlePrevProvider, handleIMEI, handleAccount, handleAltPhone
  } = mobileHandlers

  useEffect(() => {
    if (selectedSpid) {
      const selectedProvider = serviceProviders.find(
        (provider) => provider.value === selectedSpid
      )

      if (selectedProvider) {
        setMaxLengthAccountNumber(selectedProvider.max)
        setMinLengthAccountNumber(selectedProvider.min)
        setAccountNumberRegex(selectedProvider.regex)
        setAccountNumberErrorMessage(selectedProvider.hint || '')
      } else {
        setMaxLengthAccountNumber(MAX_LENGTH_ACCOUNT_NUMBER)
        setMinLengthAccountNumber(0)
        setAccountNumberRegex('')
        setAccountNumberErrorMessage('')
      }

      if (prevProviderAccountNumber) {
        validateAccountNumber()
      }
    }
  }, [currentLanguage, selectedSpid, serviceProviders])

  useEffect(() => {
    if (phoneNumberState.selectedSpidError) {
      handlePrevProvider()
    }
    if (phoneNumberState.selectedImeiError) {
      handleIMEI()
    }
    if (phoneNumberState.previousProviderAccountNumberError) {
      handleAccount(prevProviderAccountNumber)
      validateAccountNumber()
    }
    if (phoneNumberState.alternateContactNumberError) {
      handleAltPhone()
    }
  }, [currentLanguage])

  const updatePhoneNumberStore = (payload) => {
    dispatch(updatePhoneNumberState(payload))
  }

  const normalizeInputChange = (payload) => {
    if (payload?.target) {
      return payload
    }

    if (typeof payload === 'string') {
      return { target: { value: payload } }
    }

    return { target: { value: payload?.value || '' } }
  }

  const handleSpidChange = (e) => {
    const selectedSpidValue = e.target.value
    setSPIDValue(selectedSpidValue)

    const selectedProvider = serviceProviders.find(
      (provider) => provider.value === selectedSpidValue
    )

    if (selectedProvider) {
      setMaxLengthAccountNumber(selectedProvider.max)
      setMinLengthAccountNumber(selectedProvider.min)
      setAccountNumberRegex(selectedProvider.regex)
      setAccountNumberErrorMessage(selectedProvider.hint || '')
    } else {
      setMaxLengthAccountNumber(MAX_LENGTH_ACCOUNT_NUMBER)
      setMinLengthAccountNumber(0)
      setAccountNumberRegex('')
      setAccountNumberErrorMessage('')
    }
    updatePhoneNumberStore({
      [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.WIRELESS.PREVIOUS_PROVIDER_ACCOUNT_NUMBER]: '',
      [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.WIRELESS.PREVIOUS_PROVIDER_ACCOUNT_NUMBER_ERROR]: '',
      [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.WIRELESS.SELECTED_SPID]: selectedSpidValue,
      [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.WIRELESS.SELECTED_SPID_ERROR]: '',
    })
    PhoneNumberChangeHandlers[PHONE_NUMBER_EVENTS.SPID].onChange(
      e,
      SPIDS,
      updatePhoneNumberStore
    )
  }

  const validateAccountNumber = () => {
    if (!prevProviderAccountNumber) {
      return
    }

    const accountNumberLength = prevProviderAccountNumber.length

    if (
      (accountNumberRegex && !new RegExp(accountNumberRegex).test(prevProviderAccountNumber)) ||
      accountNumberLength < minLengthAccountNumber ||
      accountNumberLength > maxLengthAccountNumber
    ) {
      updatePhoneNumberStore({
        [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.WIRELESS.PREVIOUS_PROVIDER_ACCOUNT_NUMBER_ERROR]:
          accountNumberErrorMessage,
      })
    } else {
      updatePhoneNumberStore({
        [PHONE_NUMBER_STATE.TRANSFER_A_NUMBER.WIRELESS.PREVIOUS_PROVIDER_ACCOUNT_NUMBER_ERROR]: '',
      })
    }
  }

  return (
    <>
      <div ref={prevProvRef} className="w-full">
        <DatalistV2
          items={SPIDS}
          title={previousProvider}
          onChange={handleSpidChange}
          onBlur={() => handlePrevProvider()}
          selectedValue={selectedSpid}
          error={spidErrors[spidError]}
          id="phoneNumber-previousProvider"
          value={spidValue}
        />
        <div className='mt-4 [&>*>*]:!min-h-0'>
          <CardInfo>
            <Typography>
              {transferLandlineFootnote}
            </Typography>
          </CardInfo>
        </div>
      </div>

      <div className={`space-y-8 ${selectedSpid ? '' : 'opacity-50'}`}>
        <div className="space-y-8">
          <div className="flex flex-col items-stretch justify-center md:items-start lg:flex-row lg:items-end">
            <div
              ref={accountRef}
              className={`relative w-full lg:flex-1 ${imeiNumber === '' ? '' : 'opacity-50'}`}
            >
              <Input
                type="text"
                id="prevProviderAccountNumber"
                label={previousAccountNumber}
                name="Previous Provider Account Number"
                validation={showAccountValidation ? 'error' : undefined}
                maxLength={maxLengthAccountNumber}
                value={prevProviderAccountNumber}
                onBlur={() => {
                  handleAccount(prevProviderAccountNumber)
                  validateAccountNumber()
                }}
                onChange={(payload) => {
                  PhoneNumberChangeHandlers[PHONE_NUMBER_EVENTS.ACCOUNT_NUMBER].onChange(
                    normalizeInputChange(payload),
                    updatePhoneNumberStore
                  )
                }}
                disabled={!selectedSpid || imeiNumber !== ''}
                data-testid="phoneNumber-accountNumber"
              />
            </div>
            <div className="flex items-center justify-center py-2 lg:py-0 lg:px-6 lg:pb-4">
              <Typography className="font-bold text-md">{or?.toUpperCase()}</Typography>
            </div>
            <div
              ref={imeiRef}
              className={`relative w-full lg:flex-1 ${prevProviderAccountNumber === '' ? '' : 'opacity-50'}`}
            >
              <Input
                type="text"
                id="Imei"
                label={imei}
                name="Your Phone's IMEI number"
                validation={showImeiValidation ? 'error' : undefined}
                maxLength={MAX_LENGTH}
                value={imeiNumber}
                onBlur={() => handleIMEI()}
                onChange={(payload) => {
                  PhoneNumberChangeHandlers[PHONE_NUMBER_EVENTS.IMEI].onChange(
                    normalizeInputChange(payload),
                    updatePhoneNumberStore
                  )
                }}
                disabled={!selectedSpid || prevProviderAccountNumber !== ''}
                tooltip={imeiTooltip}
                onToolTipClick={toolTipHandler}
                onToolTipBlur={toolTipBlurHandler}
                data-testid="phoneNumber-imei"
              />
              {toggleTooltip && (
                <ToolTip align="top">
                  <Heading4>{imeiTooltip}</Heading4>
                </ToolTip>
              )}
            </div>
          </div>
          {errorMessage && (
            <div className='mt-2 [&>*>*]:!min-h-0'>
              <CardInfo
                testId="phoneNumber-transferError"
                tokens={{
                  backgroundColor: '#fcd3c6',
                }}
              >
                <Typography>
                  {errorMessage}
                </Typography>
              </CardInfo>
            </div>
          )}
        </div>

        <div className="w-full text-black">
          <div ref={alternateRef} className="w-full mt-2">
            <Input
              type="text"
              id="alternateContactNumber"
              name="sim-number*"
              label={`${alternateContactTitle}`}
              hint={`${important} ${brand === Brands.koodo ? alternateInfoKoodo : alternateInfoPublic}`}
              tokens={{ hintFontSize: 12 }}
              feedback={alternatePhoneError}
              validation={alternatePhoneError ? 'error' : undefined}
              maxLength={MAX_LENGTH_ALTERNATE_PHONE_NUMBER}
              placeholder="(XXX) XXX XXXX"
              value={alternateContactNumber}
              onBlur={() => handleAltPhone()}
              onChange={(payload) => {
                PhoneNumberChangeHandlers[PHONE_NUMBER_EVENTS.ALTERNATE_CONTACT_NUMBER].onChange(
                  normalizeInputChange(payload),
                  updatePhoneNumberStore
                )
              }}
              disabled={!selectedSpid}
              data-testid="phoneNumber-alternateContact"
            />
          </div>
        </div>
        <CardInfo
          heading={textContent?.additionalSecurityTitle}
        >
          <div className="py-2">
            <MarkdownRenderer
              markdown={textContent?.additionalSecurityContent}
              paragraphClassForIndex={() => 'text-copy text-sm font-body leading-6'}
              linkPrimaryClass={false}
              linkFontClass="font-body"
              onLinkClick={(text) => {
                if (pushClickAnalytics) {
                  pushClickAnalytics(text, text)
                }
              }}
            />
          </div>
        </CardInfo>
      </div>
    </>
  )
}

export default TransferMobile
